import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import { Children, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Dialog } from 'components/dialog';
import { CloseButton } from 'components/dialog/CloseButton';
import { Stepper } from 'components/onboarding/Dialog/Stepper';
import { glossary } from 'lib/glossary';

const Root = styled(Vertical)`
  height: 100%;
`;

const Wrapper = styled(Vertical)`
  justify-content: space-between;
  height: 100%;
  isolation: isolate;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 2;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const SlidingContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  transition: transform 0.3s ease;
  height: 100%;
`;

const StepperWrapper = styled(Horizontal).attrs({ center: true })`
  z-index: 1;
  position: absolute;
  width: 112px;
  align-self: center;
  padding: var(--quadruple-unit) 0 var(--double-unit);
  isolation: isolate;
`;

const FooterButtons = styled(Horizontal).attrs({ gap: 2 })`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
  &:before {
    position: absolute;
    inset: -100% 0 0 0;
    content: '';
    background: linear-gradient(
      to bottom,
      transparent 20%,
      var(--c-nl-500),
      var(--c-black)
    );
  }
  & > * {
    flex: 1;
  }
`;
const Step = styled.div`
  min-width: 100%;
  display: grid;
  > * {
    padding-top: calc(
      8 * var(--unit)
    ) !important; /** This can't be override by the component itself */
  }
  &:not(.disableFooter) > * {
    padding-bottom: calc(
      9 * var(--unit)
    ) !important; /** This can't be override by the component itself */
  }
`;

type Props = {
  open: boolean;
  onClose?: () => void;
  children:
    | ReactNode
    | ((args: { currentStep: number; onBack: () => void }) => ReactNode);
  onStepChanged?: (step: number, prevStep: number) => void;
  className?: string;
  disableClose?: boolean;
  disableSkip?: boolean;
  confirmCtaProps?: Partial<ComponentProps<typeof Button>>;
  footerCtas?: (
    | undefined
    | ((args: {
        step: number;
        onClose: () => void;
        updateStep: (step: number) => void;
      }) => ReactNode[])
  )[];
  banner?: ReactNode;
  disableFooter?: (step: number) => boolean;
  initialStep?: number;
};

export const TutorialDialog = ({
  open,
  onClose: onCloseProp,
  children,
  onStepChanged,
  className,
  disableClose,
  disableSkip,
  confirmCtaProps,
  footerCtas,
  disableFooter,
  banner,
  initialStep,
}: Props) => {
  const [step, setStep] = useState(initialStep ?? 0);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const updateStep = (s: number) => {
    onStepChanged?.(s, step);
    setStep(s);
  };
  useEffect(() => {
    const clearTimeoutFn = () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }
    };
    clearTimeoutFn();
    return clearTimeoutFn;
  }, []);

  const onClose = () => {
    onCloseProp?.();
    timeout.current = setTimeout(() => {
      setStep(0);
    }, 500);
  };

  const isFirstStep = step === 0;

  const onBack = () => {
    if (isFirstStep) {
      onClose();
    } else {
      updateStep(step - 1);
    }
  };
  const computedChildren =
    typeof children === 'function'
      ? children({
          currentStep: step,
          onBack,
        })
      : children;
  const stepsNumber = Children.toArray(computedChildren).length;
  const isLastStep = step === stepsNumber - 1;

  const footer = footerCtas?.at(step) ? (
    <FooterButtons>
      {footerCtas.at(step)?.({ updateStep, step, onClose })}
    </FooterButtons>
  ) : (
    <FooterButtons>
      {(!disableSkip || !isFirstStep) && (
        <Button
          size="medium"
          disableDebounce
          color="quaternary"
          onClick={() => {
            if (isFirstStep) {
              onClose();
            } else {
              updateStep(step - 1);
            }
          }}
        >
          <FormattedMessage
            {...(isFirstStep ? glossary.skip : glossary.back)}
          />
        </Button>
      )}
      {isLastStep ? (
        <Button
          size="medium"
          disableDebounce
          color="primary"
          onClick={() => {
            onClose();
          }}
          {...confirmCtaProps}
        >
          {confirmCtaProps?.children || (
            <FormattedMessage {...glossary.letsGo} />
          )}
        </Button>
      ) : (
        <Button
          size="medium"
          disableDebounce
          color="secondary"
          onClick={() => {
            updateStep(step + 1);
          }}
        >
          <FormattedMessage {...glossary.next} />
        </Button>
      )}
    </FooterButtons>
  );
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={disableClose ? undefined : onClose}
      hideHeader
      footer={disableFooter?.(step) ? undefined : footer}
    >
      <Root gap={0}>
        {banner}
        <Wrapper>
          <StepperWrapper>
            <Stepper step={step} length={stepsNumber} setStep={updateStep} />
          </StepperWrapper>
          {!disableClose && (
            <CloseButtonWrapper>
              <CloseButton onClose={onClose} />
            </CloseButtonWrapper>
          )}

          <SlidingContentWrapper
            style={{
              transform: `translateX(calc(-100% * ${step})`,
            }}
          >
            {Children.map(computedChildren, (item, index) => (
              <Step
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={classNames(className, {
                  disableFooter: disableFooter?.(step),
                })}
              >
                {item}
              </Step>
            ))}
          </SlidingContentWrapper>
        </Wrapper>
      </Root>
    </Dialog>
  );
};
